import { createContext, useContext, type ReactNode } from 'react';
import type { Role } from '@commissions/auth/roles';
import type { CurrentUser } from './use-current-user';

type Ctx = {
  user: CurrentUser;
  /** True when running under the dev-bypass (no real MSAL session). */
  devBypass: boolean;
  hasRole: (role: Role) => boolean;
  /**
   * Does the caller reach this QUALIFIED tab id (`<pageId>.<tab>`)?
   *
   * Reads the server-supplied `grants.tabs` from /api/me — it does NOT
   * re-derive authorization in the browser, which the deleted `can()` did. That is the
   * whole point of the rebuild: the server decides, the SPA renders the answer.
   *
   * ⚠️ Qualified ids only. `timeline` is a tab on two different pages, so a
   * bare id is ambiguous, and resolving one flatly is the collision-bypass
   * shape that already shipped once on the field axes.
   */
  reachesTab: (tab: string) => boolean;
  /**
   * Does the caller reach this PAGE id (e.g. `customers.detail`)?
   *
   * Same source and same stance as `reachesTab` — the server's `grants.pages`
   * from /api/me, never re-derived here. Page and tab are INDEPENDENT: holding
   * a page implies no tab, and a granted page with no granted tab renders
   * honestly rather than blank (Q19).
   */
  reachesPage: (page: string) => boolean;
  /**
   * May the caller perform this OPERATION (e.g. `add_note`)?
   *
   * Reads the server-supplied `grants.operations` from /api/me. An operation
   * has no field-shaped question behind it, so `_access` has nothing to say
   * about one — this is the only source, and it is available before any data
   * loads, so the affordance never renders enabled and then withdraws (Q30).
   *
   * ⚠️ Cannot answer target-dependent questions. "May I void THIS payout?"
   * depends on the payout's state, which is a 409 from the server, not a grant.
   */
  reachesOperation: (operation: string) => boolean;
  /**
   * May the caller ENUMERATE this collection (qualified `<pageId>.<list>`)?
   *
   * ⚠️ Qualified ids only, same ambiguity argument as tabs. And a list id names
   * a PROJECTION — two surfaces over the same rows with different columns are
   * two grants, so do not reuse one id for both.
   */
  reachesList: (list: string) => boolean;
};

const CurrentUserContext = createContext<Ctx | null>(null);

export function CurrentUserProvider({
  user, devBypass, children,
}: {
  user: CurrentUser;
  devBypass: boolean;
  children: ReactNode;
}) {
  const ctx: Ctx = {
    user,
    devBypass,
    hasRole: (role) => user.roles.includes(role),
    reachesTab: (tab) => (user.grants?.tabs ?? []).includes(tab),
    reachesPage: (page) => (user.grants?.pages ?? []).includes(page),
    reachesOperation: (op) => (user.grants?.operations ?? []).includes(op),
    reachesList: (list) => (user.grants?.lists ?? []).includes(list),
  };
  return <CurrentUserContext.Provider value={ctx}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUserContext(): Ctx {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error('useCurrentUserContext must be used within CurrentUserProvider');
  return ctx;
}

/**
 * The same context, `null` when no provider is mounted, for callers that must
 * not throw without one.
 *
 * ⚠️ This is for UI-KIT PRIMITIVES ONLY — components that exist below the app
 * layer and are rendered standalone by their own tests (`Modal` is the case
 * that needed it). Anything in `pages/` or `app-kit/` runs under the provider
 * and must use `useCurrentUserContext`, so a missing provider stays a loud
 * error there rather than a silently ungranted screen.
 *
 * Treat `null` as "no grants", never as "all grants" — an absent provider must
 * fail closed.
 */
export function useOptionalCurrentUser(): Ctx | null {
  return useContext(CurrentUserContext);
}
